import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, Upload, History, Trash2, Loader2, Link2, ChevronsUpDown, AlertTriangle } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

// Import van oude voorraadstanden uit Excel/CSV. Matcht op de artikelcode van de
// leverancier (fallback: SKU of EAN) en bewaart de standen als peilingen.
export function HistorischeStanden({ isAdmin }) {
  const [bestand, setBestand] = useState(null);
  const [deel, setDeel] = useState("warehouse");
  const [datum, setDatum] = useState("");
  const [analyse, setAnalyse] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imports, setImports] = useState([]);
  const [producten, setProducten] = useState([]);
  const [keuzes, setKeuzes] = useState({});   // code → product_id | "negeer"

  const loadImports = () => api.get("/voorraad/peilingen/imports")
    .then((r) => setImports(r.data || [])).catch(() => {});
  useEffect(() => { loadImports(); }, []);
  useEffect(() => {
    Promise.all([api.get("/producten"), api.get("/producten", { params: { gearchiveerd: true } })])
      .then(([a, b]) => setProducten(
        [...(a.data || []).map((p) => ({ ...p, gearchiveerd: false })),
         ...(b.data || []).map((p) => ({ ...p, gearchiveerd: true }))]
          .map((p) => ({ id: p.id, sku: p.sku, naam: p.naam, moeder_sku: p.moeder_sku || "",
                         gearchiveerd: p.gearchiveerd }))
          .sort((a2, b2) => (a2.sku || "").localeCompare(b2.sku || ""))
      )).catch(() => {});
  }, []);

  const analyseer = async (bestandOverride) => {
    const f = bestandOverride || bestand;
    if (!f) { toast.error("Kies eerst een bestand"); return; }
    setBusy(true); setAnalyse(null);
    try {
      const fd = new FormData();
      fd.append("bestand", f);
      fd.append("deel", deel);
      fd.append("datum", datum || "");
      const { data } = await api.post("/voorraad/peilingen/analyse", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setAnalyse(data);
      setKeuzes({});
      toast.success(`${data.gematcht} van ${data.totaal} regels gematcht`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse mislukt");
    } finally { setBusy(false); }
  };

  const koppelOnbekend = async () => {
    const koppelingen = Object.entries(keuzes)
      .filter(([, v]) => v && v !== "negeer")
      .map(([code, product_id]) => ({ code, product_id }));
    const negeren = Object.entries(keuzes).filter(([, v]) => v === "negeer").map(([code]) => code);
    if (!koppelingen.length && !negeren.length) { toast.error("Kies eerst een product bij de onbekende codes"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/voorraad/artikelcodes/koppel", { koppelingen, negeren });
      toast.success(`${data.gekoppeld} code(s) gekoppeld${data.genegeerd ? `, ${data.genegeerd} genegeerd` : ""}`);
      await analyseer();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Koppelen mislukt");
    } finally { setBusy(false); }
  };

  const importeer = async () => {
    const rijen = (analyse?.rijen || []).filter((r) => r.product_id && r.datum);
    if (!rijen.length) { toast.error("Geen gematchte regels met een datum"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/voorraad/peilingen/import", { bestand: analyse.bestand, rijen });
      toast.success(`${data.nieuw} nieuw, ${data.bijgewerkt} bijgewerkt`);
      setAnalyse(null); setBestand(null); loadImports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import mislukt");
    } finally { setBusy(false); }
  };

  const verwijderImport = async (id) => {
    if (!window.confirm("Deze import terugdraaien?")) return;
    await api.delete(`/voorraad/peilingen/imports/${id}`);
    toast.success("Import teruggedraaid"); loadImports();
  };

  const teImporteren = (analyse?.rijen || []).filter((r) => r.product_id && r.datum).length;

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="historische-standen">
      <div>
        <h2 className="font-display text-xl font-medium flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-primary" /> Historische voorraadstanden importeren
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Upload een oude voorraadlijst (Excel of CSV) zoals je die van je leverancier krijgt. De koprij,
          de peildatum (uit het bestand of uit de bestandsnaam) en de kolommen worden automatisch herkend.
          Regels worden gematcht op de <strong>artikelcode van de leverancier</strong> (anders op SKU of EAN).
          De huidige voorraadstanden blijven ongewijzigd — de standen komen als historie op de productpagina te staan.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-1">
          <Label className="text-xs text-muted-foreground">Bestand (.xlsx / .csv)</Label>
          <Input type="file" accept=".xlsx,.xls,.csv" disabled={!isAdmin}
            onChange={(e) => { setBestand(e.target.files?.[0] || null); setAnalyse(null); }}
            data-testid="peiling-bestand" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Peildatum (als het bestand er geen heeft)</Label>
          <Input type="date" value={datum} disabled={!isAdmin}
            onChange={(e) => setDatum(e.target.value)} data-testid="peiling-datum" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Voorraaddeel</Label>
          <Select value={deel} onValueChange={setDeel} disabled={!isAdmin}>
            <SelectTrigger data-testid="peiling-deel"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="warehouse">Eigen warehouse</SelectItem>
              <SelectItem value="leverancier">Leverancier</SelectItem>
              <SelectItem value="china">Order fabrikant (China)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {isAdmin && (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => analyseer()} disabled={busy || !bestand}
            data-testid="peiling-analyse-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Analyseer bestand
          </Button>
          {analyse && (
            <Button size="sm" onClick={importeer} disabled={busy || teImporteren === 0}
              data-testid="peiling-import-btn">
              Importeer {teImporteren} regel(s)
            </Button>
          )}
        </div>
      )}

      {analyse && (
        <div className="space-y-2" data-testid="peiling-analyse">
          <div className="text-xs text-muted-foreground">
            Blad <strong>{analyse.blad}</strong>, koprij {analyse.koprij} — code:{" "}
            <strong>{analyse.kolommen.code}</strong>, aantal: <strong>{analyse.kolommen.aantal}</strong>
            {analyse.kolommen.datum ? <> , datum: <strong>{analyse.kolommen.datum}</strong></> : null}
            <br />
            Peildatum: <strong>{analyse.peildatum || "onbekend"}</strong>{" "}
            ({analyse.peildatum_bron === "bestand" ? "uit het bestand"
              : analyse.peildatum_bron === "bestandsnaam" ? "uit de bestandsnaam"
              : analyse.peildatum_bron === "ingevuld" ? "door jou ingevuld"
              : "niet gevonden — vul hierboven een peildatum in"}) · {analyse.gematcht} gematcht ·{" "}
            {analyse.niet_gematcht} niet gematcht · {analyse.zonder_datum} zonder datum
            {analyse.genegeerd ? ` · ${analyse.genegeerd} genegeerd` : ""}
          </div>

          {analyse.niet_gematcht > 0 && (
            <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-3 space-y-2"
              data-testid="onbekende-codes">
              <div className="text-xs font-medium text-amber-900">
                {analyse.niet_gematcht} onbekende artikelcode(s) — koppel ze hier aan een product
                (ook dochterproducten en gearchiveerde producten staan in de lijst; zoek op SKU of naam),
                dan matchen ze voortaan automatisch.
              </div>
              {[...new Set(analyse.rijen.filter((r) => !r.product_id).map((r) => r.code))].map((code) => (
                <div key={code} className="flex items-center gap-2" data-testid={`onbekend-${code}`}>
                  <span className="font-mono text-xs w-32 shrink-0">{code}</span>
                  <ProductKiezer producten={producten} value={keuzes[code] || ""} disabled={!isAdmin}
                    testid={`onbekend-select-${code}`}
                    onChange={(v) => setKeuzes((k) => ({ ...k, [code]: v }))} />
                </div>
              ))}
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={koppelOnbekend} disabled={busy}
                  data-testid="onbekend-koppel-btn">
                  <Link2 className="h-4 w-4 mr-2" /> Koppel gekozen codes en analyseer opnieuw
                </Button>
              )}
            </div>
          )}
          <div className="border border-border rounded-lg overflow-hidden max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground sticky top-0">
                <tr>
                  <th className="text-left p-2">Rij</th>
                  <th className="text-left p-2">Artikelcode</th>
                  <th className="text-left p-2">Product</th>
                  <th className="text-left p-2">Datum</th>
                  <th className="text-right p-2">Aantal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {analyse.rijen.slice(0, 200).map((r) => (
                  <tr key={r.rij} className={!r.product_id || !r.datum ? "bg-red-50/60" : ""}>
                    <td className="p-2 text-xs text-muted-foreground">{r.rij}</td>
                    <td className="p-2 font-mono text-xs">{r.code}</td>
                    <td className="p-2">{r.naam || <span className="text-destructive text-xs">niet gevonden</span>}</td>
                    <td className="p-2 text-xs">{r.datum || <span className="text-destructive">geen datum</span>}</td>
                    <td className="p-2 text-right tabular-nums">{r.aantal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <GenegeerdeCodes isAdmin={isAdmin} />

      {imports.length > 0 && (        <div className="space-y-1" data-testid="peiling-imports">
          <Label className="text-xs text-muted-foreground">Eerdere imports</Label>
          {imports.map((im) => (
            <div key={im.id} className="flex items-center justify-between text-sm border border-border rounded-lg px-3 py-2">
              <span>
                {fmtDate(im.datum)} — <span className="font-medium">{im.bestand || "bestand"}</span>{" "}
                <span className="text-muted-foreground">({im.nieuw} nieuw, {im.bijgewerkt} bijgewerkt)</span>
              </span>
              {isAdmin && (
                <Button variant="ghost" size="sm" className="text-destructive"
                  onClick={() => verwijderImport(im.id)} data-testid={`peiling-import-verwijder-${im.id}`}>
                  <Trash2 className="h-3 w-3 mr-1" /> Terugdraaien
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Doorzoekbare productkiezer: zoekt op SKU, naam én moeder-SKU, en laat bij
// dochterproducten zien van welk moederproduct ze afstammen.
function ProductKiezer({ producten, value, onChange, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const gekozen = producten.find((p) => p.id === value);
  const label = value === "negeer" ? "Negeren (nooit meer tonen)"
    : gekozen ? `${gekozen.sku} — ${gekozen.naam}` : "Kies een product…";
  const nq = q.trim().toLowerCase();
  const lijst = nq
    ? producten.filter((p) => `${p.sku} ${p.naam} ${p.moeder_sku}`.toLowerCase().includes(nq))
    : producten;

  return (
    <div className="relative max-w-md w-full">
      <Button type="button" variant="outline" size="sm" disabled={disabled} data-testid={testid}
        className="h-8 w-full justify-between font-normal"
        onClick={() => { setOpen(!open); setQ(""); }}>
        <span className={`truncate ${value ? "" : "text-muted-foreground"}`}>{label}</span>
        <ChevronsUpDown className="h-3 w-3 opacity-50 shrink-0" />
      </Button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg"
          data-testid={`${testid}-lijst`}>
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op SKU of naam…"
            className="h-8 m-1 w-[calc(100%-0.5rem)]" data-testid={`${testid}-zoek`} />
          <div className="max-h-60 overflow-y-auto py-1">
            <button type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary"
              onClick={() => { onChange("negeer"); setOpen(false); }}>
              Negeren (nooit meer tonen)
            </button>
            {lijst.map((p) => (
              <button key={p.id} type="button" data-testid={`${testid}-optie-${p.sku}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary"
                onClick={() => { onChange(p.id); setOpen(false); }}>
                <span className="font-mono text-xs">{p.sku}</span> — {p.naam}
                {p.moeder_sku ? <span className="text-xs text-muted-foreground"> (dochter van {p.moeder_sku})</span> : null}
                {p.gearchiveerd ? <span className="text-xs text-amber-600"> (gearchiveerd)</span> : null}
              </button>
            ))}
            {lijst.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">Geen product gevonden</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Codes die bewust worden overgeslagen bij imports (andere merken, private label).
function GenegeerdeCodes({ isAdmin }) {
  const [codes, setCodes] = useState([]);
  const load = () => api.get("/voorraad/artikelcodes/genegeerd")
    .then((r) => setCodes(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!codes.length) return null;
  return (
    <div className="space-y-1" data-testid="genegeerde-codes">
      <Label className="text-xs text-muted-foreground">Genegeerde artikelcodes ({codes.length})</Label>
      <div className="flex flex-wrap gap-2">
        {codes.map((c) => (
          <span key={c.code} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-xs font-mono">
            {c.code}
            {isAdmin && (
              <button type="button" className="text-muted-foreground hover:text-destructive"
                data-testid={`genegeerd-verwijder-${c.code}`}
                onClick={async () => { await api.delete(`/voorraad/artikelcodes/genegeerd/${encodeURIComponent(c.code)}`); load(); }}>
                ×
              </button>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

// Zet oude verkoopfactuurregels (import oud CRM) om naar historische verkoopmutaties.
export function HistorischeVerkopen({ isAdmin }) {
  const [status, setStatus] = useState(null);
  const [jaar, setJaar] = useState("");
  const [resultaat, setResultaat] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = () => api.get("/voorraad/historische-verkopen/status")
    .then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post("/voorraad/historische-verkopen", {
        dry_run: dryRun, vanaf_jaar: jaar ? Number(jaar) : null,
      });
      setResultaat(data);
      toast.success(dryRun
        ? `Proefronde: ${data.aangemaakt} mutaties zouden worden aangemaakt`
        : `${data.aangemaakt} historische mutaties aangemaakt`);
      if (!dryRun) loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Actie mislukt");
    } finally { setBusy(false); }
  };

  const verwijder = async () => {
    if (!window.confirm("Alle historische verkoopmutaties verwijderen? De huidige voorraad verandert niet.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/voorraad/historische-verkopen");
      toast.success(`${data.verwijderd} historische mutaties verwijderd`);
      setResultaat(null); loadStatus();
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="historische-verkopen">
      <div>
        <h2 className="font-display text-xl font-medium flex items-center gap-2">
          <History className="h-5 w-5 text-primary" /> Oude verkopen als voorraadmutaties
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Maakt van elke oude verkoopfactuurregel een mutatie op de factuurdatum, gekoppeld aan het product via
          artikelcode, EAN of naam. Creditnota's worden positief geboekt. Dit is <strong>alleen historie</strong> —
          je huidige voorraadstanden en de prognose veranderen er niet door.
        </p>
        {status && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="historische-verkopen-status">
            Nu aanwezig: <strong>{status.historische_mutaties}</strong> historische mutaties
            {status.oudste ? ` (oudste ${fmtDate(status.oudste)})` : ""} · {status.factuurregels} factuurregels beschikbaar
          </p>
        )}
      </div>
      <div className="w-56">
        <Label className="text-xs text-muted-foreground">Alleen vanaf jaar (leeg = alles)</Label>
        <Input type="number" min="2000" max="2100" placeholder="bijv. 2020" value={jaar}
          disabled={!isAdmin} onChange={(e) => setJaar(e.target.value)} data-testid="historische-verkopen-jaar" />
      </div>
      {isAdmin && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => run(true)} disabled={busy}
            data-testid="historische-verkopen-dryrun">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Proefronde
          </Button>
          <Button size="sm" onClick={() => run(false)} disabled={busy}
            data-testid="historische-verkopen-run">Uitvoeren</Button>
          {(status?.historische_mutaties || 0) > 0 && (
            <Button variant="ghost" size="sm" className="text-destructive" onClick={verwijder} disabled={busy}
              data-testid="historische-verkopen-verwijder">
              <Trash2 className="h-3 w-3 mr-1" /> Alles terugdraaien
            </Button>
          )}
        </div>
      )}
      {resultaat && (
        <div className="text-xs text-muted-foreground space-y-1" data-testid="historische-verkopen-resultaat">
          <div>
            {resultaat.dry_run ? "Proefronde" : "Uitgevoerd"}: {resultaat.aangemaakt} aangemaakt ·{" "}
            {resultaat.al_aanwezig} al aanwezig · {resultaat.niet_gematcht} niet gematcht ·{" "}
            {resultaat.zonder_datum} zonder datum
          </div>
          {(resultaat.voorbeelden || []).length > 0 && (
            <div className="font-mono">
              {resultaat.voorbeelden.map((v, i) => (
                <div key={i}>{String(v.datum).slice(0, 10)} · {v.sku} · {v.aantal} · {v.referentie}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Voorraadgaten: periodes waarin de gemeten voorraad niet klopt met verkopen +
// inboekingen. Per periode kun je het gat in één klik dichten met een correctie-mutatie.
export function VoorraadGaten({ isAdmin }) {
  const [drempel, setDrempel] = useState(10);
  const [merk, setMerk] = useState("");
  const [merken, setMerken] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});

  const load = async (min, m) => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/voorraad/gaten", {
        params: { min_verschil: min ?? drempel, ...((m ?? merk) ? { merk: m ?? merk } : {}) },
      });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ophalen mislukt");
    } finally { setBusy(false); }
  };
  useEffect(() => {
    load(10, "");
    api.get("/producten/merken").then((r) => setMerken((r.data || []).map((x) => x.merk))).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const dicht = async (pid, gat) => {
    setBusy(true);
    try {
      await api.post("/voorraad/gaten/dichten",
        { product_id: pid, aantal: gat.verschil, datum: gat.midden, deel: "warehouse" });
      toast.success(`${gat.verschil > 0 ? "+" : ""}${gat.verschil} geboekt op ${fmtDate(gat.midden)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dichten mislukt");
    } finally { setBusy(false); }
  };

  const dichtAlles = async (r) => {
    if (!window.confirm(`Alle ${r.gaten.length} gaten van ${r.sku} dichten?`)) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/voorraad/gaten/dichten-alles",
        { product_id: r.product_id, min_verschil: drempel, deel: "warehouse" });
      toast.success(`${res.gedicht} gat(en) gedicht (${res.stuks} stuks)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dichten mislukt");
    } finally { setBusy(false); }
  };

  const draaiTerug = async () => {
    if (!window.confirm("Alle gedichte gaten terugdraaien?")) return;
    setBusy(true);
    try {
      const { data: r } = await api.delete("/voorraad/gaten/dichten");
      toast.success(`${r.verwijderd} correctie(s) verwijderd`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Terugdraaien mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="voorraad-gaten">
      <div>
        <h2 className="font-display text-xl font-medium flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" /> Ontbrekende inboekingen
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Per periode tussen twee gemeten standen (en tussen de laatste meting en de voorraad van vandaag) wordt
          gerekend: <strong>stand − verkopen + inboekingen</strong>. Wijkt dat af van de gemeten stand, dan mist er
          een boeking. Positief verschil = <strong>ontbrekende binnenkomst</strong>. Met "Gat dichten" zet je een
          correctie op het midden van die periode; je huidige voorraadstand blijft ongewijzigd.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Toon verschillen groter dan</Label>
          <Input type="number" min="0" className="w-28 h-8" value={drempel}
            onChange={(e) => setDrempel(Number(e.target.value))} data-testid="gaten-drempel" />
        </div>
        <Button variant="outline" size="sm" onClick={() => load()} disabled={busy} data-testid="gaten-verversen">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Verversen
        </Button>
        {merken.length > 1 && (
          <div className="inline-flex rounded-full border border-border p-0.5" data-testid="gaten-merk-filter">
            {["", ...merken].map((m) => (
              <button key={m || "alle"} type="button" data-testid={`gaten-merk-${m || "alle"}`}
                onClick={() => { setMerk(m); load(undefined, m); }}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  merk === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}>
                {m || "Alle merken"}
              </button>
            ))}
          </div>
        )}
        {isAdmin && (
          <Button variant="ghost" size="sm" className="text-destructive" onClick={draaiTerug} disabled={busy}
            data-testid="gaten-terugdraaien">
            <Trash2 className="h-3 w-3 mr-1" /> Correcties terugdraaien
          </Button>
        )}
      </div>

      {data && (
        <div className="space-y-2" data-testid="gaten-lijst">
          <div className="text-xs text-muted-foreground">
            {data.aantal_producten} product(en) met een gat · totaal{" "}
            <strong className="text-foreground">{data.totaal_ontbrekend}</strong> stuks ontbrekende binnenkomsten
          </div>
          {data.producten.length === 0 && (
            <div className="text-sm text-muted-foreground">Geen gaten gevonden — je historie sluit.</div>
          )}
          {data.producten.map((r) => (
            <div key={r.product_id} className="border border-border rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 hover:bg-secondary/50">
                <button type="button" data-testid={`gaten-product-${r.sku}`}
                  className="flex-1 flex items-center justify-between gap-3 text-left"
                  onClick={() => setOpen((o) => ({ ...o, [r.product_id]: !o[r.product_id] }))}>
                  <span className="text-sm">
                    <span className="font-mono text-xs">{r.sku}</span> — {r.naam}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {r.gaten.length} periode(s) · grootste {r.grootste} stuks
                  </span>
                </button>
                {isAdmin && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs shrink-0" disabled={busy}
                    onClick={() => dichtAlles(r)} data-testid={`gaten-dicht-alles-${r.sku}`}>
                    Alle gaten dichten
                  </Button>
                )}
              </div>
              {open[r.product_id] && (
                <div className="border-t border-border divide-y divide-border">
                  {r.gaten.map((g, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-xs">
                      <span className="w-44 shrink-0">{fmtDate(g.van)} → {fmtDate(g.tot)}</span>
                      <span className="text-muted-foreground">
                        stand {g.start_stand} − verkocht {g.verkopen} + ingeboekt {g.ingeboekt} ={" "}
                        <span className="text-foreground">{g.verwacht}</span>, {g.gemeten_bron}{" "}
                        <span className="text-foreground">{g.gemeten}</span>
                        {g.direct_geleverd ? (
                          <span className="text-sky-700"> · {g.direct_geleverd} direct geleverd (niet meegerekend)</span>
                        ) : null}
                      </span>
                      <span className={`font-medium ${g.verschil > 0 ? "text-amber-600" : "text-destructive"}`}>
                        {g.verschil > 0 ? "+" : ""}{g.verschil}
                      </span>
                      <div className="ml-auto flex items-center gap-2">
                        <Link to={`/producten/${r.product_id}`} className="text-primary hover:underline">Product</Link>
                        {isAdmin && (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy}
                            onClick={() => dicht(r.product_id, g)}
                            data-testid={`gat-dichten-${r.sku}-${i}`}>
                            Gat dichten
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
